<template>
  <UikitDialog
    :model-value="modelValue"
    full-height
    position="right"
    :hide-close="!isDesktop"
    content-class="osk-products-filters-dialog"
    slide-right
    @update:model-value="handleClose"
    @handle-close="handleCloseAll"
  >
    <div class="osk-products-filters-dialog__wrapper">
      <div class="osk-products-filters-dialog__header">
        <UikitIcon
          v-if="filtersModelProp || !isDesktop"
          name="ArrowBack"
          size="l"
          @click="handleClose"
        />

        <div
          class="osk-products-filters-dialog__refresh"
          @click="handleReset"
        >
          {{ $t('components.business.products.filters.resetAll') }}
        </div>
      </div>

      <div
        ref="$content"
        :class="{
          'osk-products-filters-dialog__content': true,
          'osk-products-filters-dialog__content_price': filterItem && isPriceFilter(filterInfoData),
        }"
      >
        <template v-if="filterItem">
          <UikitLoaderInner v-if="getFilterInfoPending" />

          <template v-else>
            <BusinessProductsFiltersCategories
              v-if="isCategoryTreeFilter(filterInfoData)"
              :filter-info="filterInfoData"
              :title="$t('components.business.products.filters.categories')"
              :loading="getAvailableFiltersPending"
              :items-count="itemsCount"
              :page="page"
              @update-filter="updateFilter(filterInfoData, $event)"
              @close-all="handleCloseAll"
              @apply="handleApply"
            />

            <BusinessProductsFiltersMultiList
              v-if="isMultiListFilter(filterInfoData)"
              :filter-info="filterInfoData"
              :content="$content"
              :page="page"
              @update-filter="updateFilter(filterInfoData, $event)"
            />

            <BusinessProductsFiltersSize
              v-if="isSizeFilter(filterInfoData)"
              :filter-info="filterInfoData"
              :loading="getAvailableFiltersPending"
              :items-count="itemsCount"
              :page="page"
              @update-filter="updateFilter(filterInfoData, $event)"
              @close-all="handleCloseAll"
              @apply="handleApply"
            />

            <BusinessProductsFiltersPrice
              v-if="isPriceFilter(filterInfoData)"
              :filter-info="filterInfoData"
              :page="page"
              @update-filter="updateFilter(filterInfoData, $event)"
            />
          </template>
        </template>

        <template v-else>
          <BusinessProductsFiltersSorting
            v-if="productFilters?.sorting && sortingModel"
            v-model="sortingModel"
            :sorting="productFilters?.sorting"
            :page="page"
          />

          <UikitList
            v-if="filtersModel?.length"
            :title="$t('components.business.products.filters.filters')"
          >
            <template
              v-for="(item) in filtersModel"
              :key="item.code"
            >
              <BusinessProductsFiltersItemCategories
                v-if="isCategoryTreeFilter(item)"
                :item="item"
                @open-filter="openDialog"
              />

              <BusinessProductsFiltersItemMultiList
                v-if="isMultiListFilter(item)"
                v-model="item.selectedValues"
                :item="item"
                :page="page"
                @open-filter="openDialog"
              />

              <BusinessProductsFiltersItemSize
                v-if="isSizeFilter(item)"
                v-model="item.selectedValues"
                :item="item"
                @open-filter="openDialog"
              />

              <BusinessProductsFiltersItemPrice
                v-if="isPriceFilter(item)"
                :item="item"
                @open-filter="openDialog"
              />

              <BusinessProductsFiltersItemBoolean
                v-if="isBooleanFilter(item)"
                v-model="item.value"
                :item="item"
                :disabled="!item.isEnabled || getAvailableFiltersPending"
                :page="page"
              />
            </template>
          </UikitList>
        </template>
      </div>

      <div class="osk-products-filters-dialog__footer">
        <UikitButton
          full-width
          :disabled="!itemsCount"
          :loading="getAvailableFiltersPending"
          :size="isDesktop ? 'm' : 'l'"
          @click="handleClick"
        >
          {{ $t('components.business.products.filters.show') }}
          <template v-if="itemsCount">
            ({{ formatNumber(itemsCount) }})
          </template>
        </UikitButton>
      </div>
    </div>

    <BusinessProductsFiltersDialog
      v-model="showFiltersDialog"
      :product-filters="productFilters"
      :presets="presets"
      :base-category="baseCategory"
      :filter-item="filterItemSecond"
      :filters-model-prop="filtersModel"
      :items-count="itemsCount"
      :page="page"
      @update-model="handleUpdateModel"
      @close-all="handleCloseAll"
      @apply="handleApply"
    />
  </UikitDialog>
</template>

<script setup lang="ts">
import {
  ProductFilters, ProductFilterRequest,
  ProductFilter, ListValue, PriceValue, ProductSorting,
} from '~/restAPI/data-contracts';

import {
  isCategoryTreeFilter,
  isMultiListFilter,
  isSizeFilter,
  isBooleanFilter,
  isPriceFilter,
} from '~/utils/filters';
import type { TFiltersPresets } from '~/types/filters';

import { AnalyticsEvents } from '~/types/analytics';

const { $addEvent } = useNuxtApp();

const { isDesktop } = useUiBreakpoints();

const props = defineProps<{
  modelValue: boolean
  productFilters?: ProductFilters
  presets?: TFiltersPresets
  baseCategory?: IBreadCrumbsItem
  filterItem?: ProductFilter
  filtersModelProp?: ProductFilter[]
  itemsCount?: number
  sorting?: ProductSorting['code']
  page?: string
}>();

const emits = defineEmits<{
  (e: 'update:modelValue', value: boolean): void,
  (e: 'updateFilters', value?: ProductFilterRequest, filters?: ProductFilters | null): void,
  (e: 'updateModel', value?: ProductFilter[], count?: number): void,
  (e: 'closeAll'): void,
  (e: 'apply', value?: ProductFilter[], count?: number): void,
}>();

const {
  filtersModel,
  sortingModel,
  filtersRequest,
  getAvailableFiltersPending,
  itemsCount,
  generateSorting,
  generateFilters,
  getAvailableFilters,
  availableFiltersData,
  shouldAvailableFiltersLoad,
  generateItemsCount,
  filterInfoData,
  getFilterInfoPending,
  getFilterInfo,
  cancelGetFilters,
} = useProductsFiltersDialog(
  toRef(props, 'productFilters'),
  toRef(props, 'baseCategory'),
  toRef(props, 'filterItem'),
  toRef(props, 'presets'),
  toRef(props, 'sorting'),
);

const $content = ref<HTMLElement>();
const showFiltersDialog = ref(false);
const filterItemSecond = ref<ProductFilter>();

function formatNumber(val: number) {
  return val?.toLocaleString('ru-RU');
}

function handleClose() {
  if (props.filtersModelProp) {
    emits('updateModel', filtersModel.value, itemsCount.value);
  }

  emits('update:modelValue', false);
}

function handleClick() {
  $addEvent(AnalyticsEvents.FiltersLinkClick, {
    chapter: 'show',
    value: String(itemsCount.value),
    page: props.page,
  });

  if (props.filtersModelProp) {
    emits('apply', filtersModel.value, itemsCount.value);
  } else {
    emits('updateFilters', filtersRequest.value, availableFiltersData.value);
  }

  emits('update:modelValue', false);
}

function handleApply(newModel?: ProductFilter[], count?: number) {
  if (newModel) {
    handleUpdateModel(newModel, count);
  }

  handleClick();
}

async function handleUpdateModel(newModel?: ProductFilter[], count?: number) {
  shouldAvailableFiltersLoad.value = false;
  filtersModel.value = newModel;
  itemsCount.value = count;
}

function updateFilter(item: ProductFilter, value?: ListValue[] | PriceValue) {
  if (props.filterItem && filtersModel.value) {
    const filter = filtersModel.value.find((i) => i.code === item.code);

    shouldAvailableFiltersLoad.value = true;

    if (isMultiListFilter(filter) || isCategoryTreeFilter(filter) || isSizeFilter(filter)) {
      filter.selectedValues = value as ListValue[];
    }

    if (isPriceFilter(filter)) {
      const val = value as PriceValue;

      filter.selectedLower = filter.lower !== val.lower ? val.lower : undefined;
      filter.selectedUpper = filter.upper !== val.upper ? val.upper : undefined;
    }
  }
}

function handleReset() {
  $addEvent(AnalyticsEvents.FiltersLinkClick, {
    action: 'clear_all',
    page: props.page,
  });

  if (props.filterItem && filterInfoData.value) {
    const filter = filterInfoData.value;

    if (isCategoryTreeFilter(filter) || isMultiListFilter(filter) || isSizeFilter(filter)) {
      filterInfoData.value = { ...filter, selectedValues: [] };
    }

    if (isPriceFilter(filterInfoData.value)) {
      filterInfoData.value.selectedLower = undefined;
      filterInfoData.value.selectedUpper = undefined;
    }

    return;
  }

  if (sortingModel.value) {
    sortingModel.value = 'NEW';
  }

  if (filtersModel.value) {
    filtersModel.value?.forEach((filter) => {
      if (isBooleanFilter(filter)) {
        // eslint-disable-next-line no-param-reassign
        filter.value = false;
      }

      if (isMultiListFilter(filter) || isCategoryTreeFilter(filter) || isSizeFilter(filter)) {
        // eslint-disable-next-line no-param-reassign
        filter.selectedValues = [];
      }

      if (isPriceFilter(filter)) {
        // eslint-disable-next-line no-param-reassign
        filter.selectedLower = undefined;
        // eslint-disable-next-line no-param-reassign
        filter.selectedUpper = undefined;
      }
    });
  }
}

function openDialog(filter: ProductFilter) {
  filterItemSecond.value = filter;
  showFiltersDialog.value = true;
}

function handleCloseAll() {
  emits('update:modelValue', false);
  emits('closeAll');

  $addEvent(AnalyticsEvents.FiltersLinkClick, {
    action: 'close',
    page: props.page,
  });
}

watch(() => props.modelValue, (value) => {
  if (value) {
    generateSorting();
    generateItemsCount(props.itemsCount);
    generateFilters(props.filtersModelProp);

    if (props.filterItem) {
      getFilterInfo();
    }

    $addEvent(AnalyticsEvents.FiltersView, {
      page: props.page,
      id_item: props.filterItem?.code ?? 'all',
    });
  } else {
    sortingModel.value = undefined;
    filtersModel.value = undefined;
    filterInfoData.value = undefined;
    itemsCount.value = undefined;
    availableFiltersData.value = undefined;
    shouldAvailableFiltersLoad.value = true;
  }
});

watchDebounced(filtersModel, async (value, oldValue, onCleanup) => {
  if (!value || !oldValue) return;

  onCleanup(() => {
    if (getAvailableFiltersPending.value) {
      cancelGetFilters();
    }
  });

  if (!shouldAvailableFiltersLoad.value) {
    shouldAvailableFiltersLoad.value = true;
    return;
  }

  getAvailableFilters();
}, { deep: true, debounce: 300 });
</script>

<style lang="scss">
@import "~/assets/scss/settings/index";

.osk-products-filters-dialog {
  @include font-body-all;

  padding-top: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;

  @include media-query(lg-and-up) {
    width: 504px !important;
  }

  .osk-dialog__close {
    right: 50px !important;
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;

    @include media-query(lg-and-up) {
      padding-top: 94px;
    }
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    padding: 10px 16px;

    @include media-query(lg-and-up) {
      padding: 0 52px;
    }

    .osk-icon {
      cursor: pointer;
      margin-left: -6px;

      &:hover {
        opacity: .6;
      }
    }
  }

  &__refresh {
    color: $grey;
    cursor: pointer;

    @include media-query(lg-and-up) {
      margin-left: auto;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    height: 100%;
    padding: 22px $indent-medium 0;
    position: relative;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    @include media-query(lg-and-up) {
      padding: 26px 52px 16px;
    }

    &_price {
      @include media-query(lg-and-up) {
        height: auto;
        padding-top: 0;
        margin-top: auto;
        overflow: visible;

        & + .osk-products-filters-dialog__footer {
          margin-bottom: auto;
          padding-top: 8px;
        }
      }
    }
  }

  &__footer {
    padding: $indent-medium $indent-medium 0;

    @include media-query(lg-and-up) {
      padding: $indent-medium 52px 22px;
    }
  }
}
</style>
